import { useCallback, useEffect, useState } from 'react';
import {
  createFinanceAccount,
  deleteFinanceAccount,
  getBalanceHistory,
  getFinanceOverview,
  getReconcile,
  updateFinanceAccount,
  type AccountGroup,
  type AccountGroupKey,
  type BalanceHistory,
  type BankStatus,
  type BreakdownSide,
  type FinanceOverview,
  type OverviewAccount,
  type ReconcileAccount,
  type ReconcileResult,
} from '../api.js';
import { Button, Card, ErrorState, IconTile, Row, Segmented, Sheet } from '../ui/index.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { dollars, money } from './format.js';
import NetWorthChart, { GROUP_COLORS } from './NetWorthChart.js';
import { ACCOUNT_TYPES, ago, BankFooter, ConnectBank, parseCents, SkeletonGroup } from './parts.js';
import Advice from './Advice.js';

/**
 * Overview (Monarch's "Accounts" page): whether every account adds up, net
 * worth and how it moved, one card per kind of account with what changed this
 * month, what's owned against what's owed, then Advice.
 */

type NwRange = '1M' | '3M' | 'YTD' | '1Y' | 'All';
const NW_RANGES: readonly { value: NwRange; label: string }[] = [
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: 'YTD', label: 'YTD' },
  { value: '1Y', label: '1Y' },
  { value: 'All', label: 'All' },
];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Window start + bucket size: daily for a month, weekly for a quarter, monthly beyond. */
export function historyWindow(r: NwRange, now = new Date()): { from: string; interval: BalanceHistory['interval'] } {
  const back = (days: number) => ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() - days));
  switch (r) {
    case '1M':
      return { from: back(30), interval: 'day' };
    case '3M':
      return { from: back(91), interval: 'week' };
    case 'YTD':
      return { from: `${now.getFullYear()}-01-01`, interval: 'month' };
    case '1Y':
      return { from: back(365), interval: 'month' };
    case 'All':
      return { from: '2020-01-01', interval: 'month' };
  }
}

/**
 * Colour a change as it reads next to the figure shown. The server reports
 * changes in shown terms — debts as the amount owed — so for a debt, down is good.
 */
export function shownChange(change: number | null, liability: boolean): { cents: number; good: boolean } | null {
  if (change == null || change === 0) return null;
  return { cents: change, good: liability ? change < 0 : change > 0 };
}

const GROUP_ICON: Record<AccountGroupKey, Parameters<typeof IconTile>[0]['name']> = {
  cash: 'wallet',
  credit: 'receipt',
  investment: 'trend',
  vehicle: 'target',
  property: 'archive',
  loan: 'clipboard',
};

const VEHICLES: AccountGroup = { key: 'vehicle', label: 'Vehicles', liability: false, totalCents: 0, changeCents: null, accounts: [] };

/** Vehicles always get a card (that's where one is added), after the other assets. */
function withVehicles(groups: AccountGroup[]): AccountGroup[] {
  if (groups.some((g) => g.key === 'vehicle')) return groups;
  const firstDebt = groups.findIndex((g) => g.liability);
  const at = firstDebt === -1 ? groups.length : firstDebt;
  return [...groups.slice(0, at), VEHICLES, ...groups.slice(at)];
}

/** "Oct 20" from 'YYYY-MM-DD'. */
function sinceLabel(ymd: string | null): string {
  if (!ymd) return '';
  return new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Accounts the check can speak to: `market` moves without transactions and `no-data` has no anchor yet. */
export function reconcileSummary(r: ReconcileResult): { checked: number; gaps: number; offCents: number } {
  const checked = r.accounts.filter((a) => a.status === 'ok' || a.status === 'gap');
  const gaps = checked.filter((a) => a.status === 'gap');
  return { checked: checked.length, gaps: gaps.length, offCents: gaps.reduce((s, a) => s + Math.abs(a.gapCents ?? 0), 0) };
}

/** Outstanding is SUM(amount): a −$115 purchase not yet repaid is money owed back to him. */
export function frontedText(outstandingCents: number): string {
  return outstandingCents < 0
    ? `Fronted for others: ${money(-outstandingCents)} owed to you`
    : `Fronted for others: ${money(outstandingCents)} repaid beyond what you fronted`;
}

function ReconcileBar({ result }: { result: ReconcileResult }): JSX.Element | null {
  const { checked, gaps, offCents } = reconcileSummary(result);
  const fronted = result.fronted.outstandingCents;
  if (checked === 0 && fronted === 0) return null;
  return (
    <div className="mn-rec" role="status">
      {checked > 0 &&
        (gaps === 0 ? (
          <p className="mn-rec__line mn-rec__line--ok">
            <span aria-hidden="true">✓</span> All {checked} {checked === 1 ? 'account balances' : 'accounts balance'}
          </p>
        ) : (
          <p className="mn-rec__line mn-rec__line--gap">
            {gaps} {gaps === 1 ? "account doesn't" : "accounts don't"} add up · <span className="num">{money(offCents)}</span>
          </p>
        ))}
      {fronted !== 0 && <p className="mn-rec__line">{frontedText(fronted)}</p>}
    </div>
  );
}

/** Dot (green synced, amber stale, grey manual), when it last updated, and ✓ or how far off. */
function ReconcileLine({ r }: { r: ReconcileAccount }): JSX.Element {
  const tone = r.source === 'manual' ? 'manual' : r.stale ? 'stale' : 'fresh';
  const dotLabel = tone === 'manual' ? 'Manual' : tone === 'stale' ? 'Sync is stale' : 'Synced';
  return (
    <span className="mn-rec__acct">
      <span className={`mn-rec__dot mn-rec__dot--${tone}`} role="img" aria-label={dotLabel} />
      {r.syncedAt ? `Updated ${ago(r.syncedAt)}` : 'Never updated'}
      {r.status === 'ok' && (
        <span className="mn-rec__ok" role="img" aria-label="Balances">
          {' '}
          ✓
        </span>
      )}
      {r.status === 'gap' && r.gapCents != null && (
        <span className="mn-rec__off">
          {' · '}
          {money(Math.abs(r.gapCents))} off{r.since ? ` since ${sinceLabel(r.since)}` : ''}
        </span>
      )}
    </span>
  );
}

function Change({ c, suffix }: { c: { cents: number; good: boolean } | null; suffix?: string }): JSX.Element | null {
  if (!c) return null;
  return (
    <span className={`mn-chg ${c.good ? 'mn-chg--good' : 'mn-chg--bad'}`}>
      {money(c.cents, { sign: true })}
      {suffix}
    </span>
  );
}

export default function Overview({
  bank,
  simplefin,
  onChange,
  onAddAccount,
}: {
  bank: BankStatus | null;
  simplefin: boolean;
  onChange: () => void;
  onAddAccount: () => void;
}): JSX.Element {
  const [overview, setOverview] = useState<FinanceOverview | null>(null);
  const [reconcile, setReconcile] = useState<ReconcileResult | null>(null);
  const [range, setRange] = useState<NwRange>('3M');
  const [history, setHistory] = useState<BalanceHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [editing, setEditing] = useState<OverviewAccount | null>(null);
  /** Bumped after an edit so the chart refetches along with the cards. */
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      setOverview(await getFinanceOverview());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
    // The check is a layer on top; if it fails the accounts still show, just without it.
    getReconcile().then(setReconcile, () => setReconcile(null));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let live = true;
    const w = historyWindow(range);
    getBalanceHistory(w.from, w.interval).then(
      (h) => live && setHistory(h),
      () => live && setHistory(null),
    );
    return () => {
      live = false;
    };
  }, [range, version]);

  const changed = (): void => {
    void load();
    setVersion((v) => v + 1);
    onChange();
  };

  if (error && !overview) return <ErrorState message={error} onRetry={() => void load()} />;
  if (!overview) return <SkeletonGroup />;

  const netChange = overview.netChangeCents;
  const checks = new Map((reconcile?.accounts ?? []).map((r) => [r.accountId, r]));

  return (
    <>
      {reconcile && <ReconcileBar result={reconcile} />}

      <Card className="mn-hero mn-nw" label="Net worth">
        <div className="mn-nw__top">
          <div className="hb-hero__text">
            <span className="hb-hero__label">Net worth</span>
            <span className="hb-hero__value">{dollars(overview.netCents)}</span>
            {netChange != null && netChange !== 0 && (
              <span className="hb-hero__sub">
                <span className={`num ${netChange > 0 ? 'mn-chg--good' : 'mn-chg--bad'}`}>{dollars(netChange, { sign: true })}</span> this month
              </span>
            )}
          </div>
          <div className="mn-nw__range">
            <Segmented label="Net worth range" value={range} options={NW_RANGES} onChange={setRange} />
          </div>
        </div>
        {history && history.buckets.length > 1 ? (
          <NetWorthChart history={history} />
        ) : (
          <p className="hb-hint mn-nw__empty">History builds from each day's balances — check back after a few syncs.</p>
        )}
      </Card>

      <div className="mn-accts">
        {withVehicles(overview.groups).map((g) => (
          <GroupCard
            key={g.key}
            group={g}
            onAddVehicle={g.key === 'vehicle' ? () => setAddingVehicle(true) : undefined}
            onEdit={setEditing}
            checks={checks}
          />
        ))}
      </div>

      <div className="mn-acctfoot">
        {simplefin && bank ? <BankFooter bank={bank} onChange={changed} /> : <span />}
        <button type="button" className="ui-group__action" onClick={onAddAccount}>
          Add account
        </button>
      </div>

      {(overview.assets.totalCents > 0 || overview.liabilities.totalCents > 0) && (
        <Breakdown assets={overview.assets} liabilities={overview.liabilities} />
      )}

      {!simplefin && <ConnectBank onDone={changed} />}

      <section className="ui-groupwrap" aria-label="Advice">
        <div className="ui-group__head">
          <h2 className="ui-group__title">Advice</h2>
        </div>
        <Advice />
      </section>

      {addingVehicle && (
        <Sheet title="Add vehicle" onClose={() => setAddingVehicle(false)}>
          <VehicleForm
            onDone={() => {
              setAddingVehicle(false);
              changed();
            }}
          />
        </Sheet>
      )}
      {editing && (
        <Sheet title={editing.name} onClose={() => setEditing(null)}>
          <EditAccount
            account={editing}
            onDone={() => {
              setEditing(null);
              changed();
            }}
          />
        </Sheet>
      )}
    </>
  );
}

function GroupCard({
  group,
  onAddVehicle,
  onEdit,
  checks,
}: {
  group: AccountGroup;
  onAddVehicle?: () => void;
  onEdit: (a: OverviewAccount) => void;
  checks: ReadonlyMap<string, ReconcileAccount>;
}): JSX.Element {
  const mode = useThemeMode();
  const color = GROUP_COLORS[mode][group.key] ?? 'var(--accent)';
  const typeLabel = (t: string) => ACCOUNT_TYPES.find(([v]) => v === t)?.[1] ?? t;
  return (
    <section className="ui-groupwrap mn-acct" aria-label={group.label}>
      <div className="ui-group__head mn-acct__head">
        <h2 className="ui-group__title">{group.label}</h2>
        <span className="mn-acct__sum">
          <span className="mn-acct__total num">{dollars(group.totalCents)}</span>
          <Change c={shownChange(group.changeCents, group.liability)} suffix=" this month" />
        </span>
        {onAddVehicle && (
          <button type="button" className="ui-group__action" onClick={onAddVehicle}>
            Add
          </button>
        )}
      </div>
      <ul className="ui-group">
        {group.accounts.map((a) => {
          const manual = a.source === 'manual';
          const bal = a.balanceCents;
          const check = checks.get(a.id);
          const meta = [a.institution, typeLabel(a.type), a.mask ? `••${a.mask}` : null].filter(Boolean).join(' · ');
          return (
            <Row
              key={a.id}
              leading={<IconTile name={GROUP_ICON[group.key] ?? 'wallet'} color={color} />}
              title={a.name}
              sub={
                check ? (
                  <>
                    {meta && <span className="mn-rec__meta">{meta}</span>}
                    <ReconcileLine r={check} />
                  </>
                ) : (
                  meta
                )
              }
              detail={
                <span className="mn-acct__amt">
                  <span className="hb-amount">{money(bal == null ? null : group.liability ? Math.abs(bal) : bal)}</span>
                  <Change c={shownChange(a.changeCents, group.liability)} />
                </span>
              }
              onClick={manual ? () => onEdit(a) : undefined}
              chevron={manual}
              ariaLabel={manual ? `${a.name}, ${money(bal)}. Update balance` : undefined}
            />
          );
        })}
        {group.accounts.length === 0 && onAddVehicle && (
          <Row
            leading={<IconTile name="plus" color={color} />}
            title="Add vehicle"
            sub="Name it and give it a value — update it whenever you like"
            onClick={onAddVehicle}
            tone="accent"
          />
        )}
      </ul>
    </section>
  );
}

function Breakdown({ assets, liabilities }: { assets: BreakdownSide; liabilities: BreakdownSide }): JSX.Element {
  const mode = useThemeMode();
  const side = (title: string, s: BreakdownSide) => {
    const parts = s.byGroup.filter((g) => g.cents > 0);
    const pct = (c: number) => (s.totalCents > 0 ? (c / s.totalCents) * 100 : 0);
    return (
      <div className="mn-bd__side">
        <div className="mn-bd__head">
          <span className="mn-bd__title">{title}</span>
          <span className="mn-bd__total num">{dollars(s.totalCents)}</span>
        </div>
        <div className="mn-bd__bar" aria-hidden="true">
          {parts.map((g) => (
            <span key={g.key} style={{ width: `${pct(g.cents)}%`, background: GROUP_COLORS[mode][g.key] ?? '#8ea4c2' }} />
          ))}
        </div>
        {parts.length === 0 ? (
          <p className="hb-hint">None</p>
        ) : (
          <ul className="mn-bd__rows">
            {parts.map((g) => (
              <li key={g.key} className="mn-bd__row">
                <span className="mn-bd__dot" style={{ background: GROUP_COLORS[mode][g.key] ?? '#8ea4c2' }} aria-hidden="true" />
                <span className="mn-bd__label">{g.label}</span>
                <span className="mn-bd__cents num">{dollars(g.cents)}</span>
                <span className="mn-bd__pct num">{Math.round(pct(g.cents))}%</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  return (
    <section className="ui-groupwrap">
      <div className="ui-group__head">
        <h2 className="ui-group__title">Assets vs liabilities</h2>
      </div>
      <div className="ui-card mn-bd">
        {side('Assets', assets)}
        {side('Liabilities', liabilities)}
      </div>
    </section>
  );
}

function VehicleForm({ onDone }: { onDone: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = parseCents(value);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cents == null) return;
    setBusy(true);
    setError(null);
    try {
      await createFinanceAccount({ name: name.trim(), type: 'vehicle', balanceCents: cents });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="hb-form" onSubmit={submit}>
      <label className="hb-field">
        <span>Vehicle</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="2021 Tacoma" required />
      </label>
      <label className="hb-field">
        <span>Value</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="$28,500" inputMode="decimal" required />
      </label>
      <p className="hb-hint">A Kelley Blue Book private-party figure is a fair guess. Update it any time.</p>
      {error && <p className="hb-form__error" role="alert">{error}</p>}
      <Button type="submit" size="lg" block disabled={busy || !name.trim() || cents == null}>
        {busy ? 'Adding…' : 'Add vehicle'}
      </Button>
    </form>
  );
}

function EditAccount({ account, onDone }: { account: OverviewAccount; onDone: () => void }): JSX.Element {
  const [value, setValue] = useState(account.balanceCents == null ? '' : (account.balanceCents / 100).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = parseCents(value);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="hb-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (cents != null) void run(() => updateFinanceAccount(account.id, { balanceCents: cents }));
      }}
    >
      <label className="hb-field">
        <span>Balance</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" autoFocus />
      </label>
      {error && <p className="hb-form__error" role="alert">{error}</p>}
      <Button type="submit" size="lg" block disabled={busy || cents == null}>
        Save balance
      </Button>
      <Button
        variant={confirming ? 'destructive' : 'secondary'}
        block
        disabled={busy}
        onClick={() => (confirming ? void run(() => deleteFinanceAccount(account.id)) : setConfirming(true))}
      >
        {confirming ? `Delete ${account.name} for good?` : 'Delete account'}
      </Button>
    </form>
  );
}
